import { AxiosResponse } from 'axios'
import { captureException } from '@sentry/react'
import { UseMutationResult, useMutation } from '@tanstack/react-query'
import { getHttpStatus } from '../../../../api/chat'
import { RoomHeader } from '../../../../generated/api/chat-room'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { useRoomMessagesCache } from '../useRoomMessagesCache'

interface RoomHeaderMutationOptions {
  successMessage?: string
  errorMessage: string | ((error: unknown) => string)
  onSuccess?: (header: RoomHeader) => void
}

export const useRoomHeaderMutation = <TVariables>(
  mutationFn: (
    variables: TVariables
  ) => Promise<AxiosResponse<{ data: RoomHeader }>>,
  { successMessage, errorMessage, onSuccess }: RoomHeaderMutationOptions
): UseMutationResult<RoomHeader, unknown, TVariables> => {
  const { setToastNotification } = useToastNotifications()
  const { applyHeader } = useRoomMessagesCache()

  return useMutation({
    mutationFn: async (variables: TVariables) =>
      (await mutationFn(variables)).data.data,
    meta: { skipGlobalInvalidate: true },
    onSuccess: (header) => {
      applyHeader(header)
      if (successMessage) {
        setToastNotification(ToastType.SUCCESS, successMessage)
      }
      onSuccess?.(header)
    },
    onError: (error) => {
      setToastNotification(
        ToastType.DANGER,
        typeof errorMessage === 'function' ? errorMessage(error) : errorMessage
      )
      if (getHttpStatus(error) !== 400) captureException(error)
    },
  })
}
